import { escapeHtml, safeUrl } from './format.js';
import { groupBySection, type MenuBlock, type MenuPage } from './paginate.js';
import { FONT_VARS, SHAPE_CLIP, designColor, type CustomDesign, type DesignColor, type DesignFontRole } from './custom.js';
import { badge, bilingual, description, lineCount, listCss, listItem, priceBlock, type RenderContext } from './templates/common.js';
import type { MenuBoardContent, MenuItemContent, MenuOrientation, MenuSection } from './types.js';

/**
 * "Build your own" boards: a grid of ready-made blocks (item cards, section lists, headings,
 * pictures, texts, stickers) that people drag into place in the board editor. Unlike a custom
 * design, the layout belongs to the board (`MenuBoardContent.blocks`) and is a grid rather than
 * a free canvas, so it stays tidy, reflows for portrait screens and pages through long menus.
 *
 * One grid serves both orientations: blocks keep their order and spans, and the grid has a
 * column count for landscape and another for portrait (spans wider than the grid are narrowed
 * to it). Blocks are placed in order, row by row, like CSS grid's auto-placement, so moving a
 * block is just moving it in the list. On an Arabic board the grid mirrors, right to left, and
 * so do the cards ("photo on the left" puts it on the reading side: the right).
 *
 * Content is never typed into a block: an item card shows a chosen item of the board or "the
 * next item of section X", and a list shows a section's items. When a board has more items than
 * one screen of blocks takes, the layout repeats on further pages with the next items, like the
 * predefined designs do.
 *
 * Sizes are pixels on a 1920×1080 (or 1080×1920) screen, drawn in `em` at 16px per em, so the
 * board looks the same on a 4K panel and in the dashboard's preview. Colours are hex or theme
 * tokens, as in custom designs. Every number is clamped and unknown block types are skipped
 * when drawing, so a layout from a newer dashboard never breaks an older pack.
 */

export const BLOCKS_TEMPLATE_ID = 'blocks';
export const BLOCKS_LAYOUT_VERSION = 1;
export const BLOCKS_MAX_COLUMNS = 6;
export const BLOCKS_MAX_ROWS = 12;
export const BLOCKS_MAX_SPAN_ROWS = 8;
export const BLOCKS_MAX = 48;
/** How many pages a layout repeats over before it stops, whatever is left. */
const MAX_PAGES = 30;

export const BLOCK_TYPES = ['item', 'heading', 'list', 'image', 'text', 'badge', 'spacer'] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];
export const BLOCK_PHOTO_POSITIONS = ['left', 'right', 'top', 'bottom', 'background', 'none'] as const;
export type BlockPhotoPosition = (typeof BLOCK_PHOTO_POSITIONS)[number];
export const BLOCK_PHOTO_SHAPES = ['square', 'rounded', 'circle'] as const;
export const BLOCK_ALIGNS = ['start', 'center', 'end'] as const;
export type BlockAlign = (typeof BLOCK_ALIGNS)[number];
export const BLOCK_PRICE_STYLES = ['plain', 'pill', 'circle'] as const;
export const BLOCK_ORNAMENTS = ['none', 'underline', 'lines', 'dots'] as const;
export const BLOCK_LEADERS = ['dotted', 'dashed', 'none'] as const;
export const BLOCK_BADGE_SHAPES = ['pill', 'circle', 'burst', 'ribbon', 'rect'] as const;
export const BLOCK_HEADING_BINDS = ['section', 'text', 'title', 'brand', 'tagline'] as const;
export const BLOCK_FONTS = ['heading', 'body', 'script'] as const;

/** The frame every block sits in: its background, border, corners, inner space and shadow. */
export interface BlockBox {
  /** Null for none (the grid's background shows through). */
  background: DesignColor | null;
  borderColor: DesignColor;
  borderWidth: number;
  radius: number;
  padding: number;
  shadow: boolean;
}

/** A chosen item of the board, or the next item not yet shown from a section (null: any). */
export type BlockItemSource = { kind: 'item'; itemId: string } | { kind: 'next'; sectionId: string | null };

interface BlockBase {
  id: string;
  colSpan: number;
  rowSpan: number;
  box: BlockBox;
}

export interface ItemBlock extends BlockBase {
  type: 'item';
  source: BlockItemSource;
  photo: BlockPhotoPosition;
  photoShape: (typeof BLOCK_PHOTO_SHAPES)[number];
  /** Share of the card the photo takes, in percent (of its width beside the text, height above). */
  photoSize: number;
  /** Darkening over a background photo, 0–0.95, so the text stays readable. */
  overlay: number;
  showName: boolean;
  showDescription: boolean;
  showPrice: boolean;
  showSizes: boolean;
  showBadges: boolean;
  font: DesignFontRole;
  nameSize: number;
  textSize: number;
  priceSize: number;
  nameColor: DesignColor;
  textColor: DesignColor;
  priceColor: DesignColor;
  priceStyle: (typeof BLOCK_PRICE_STYLES)[number];
  /** The pill or circle behind the price. */
  priceFill: DesignColor;
  align: BlockAlign;
}

export interface HeadingBlock extends BlockBase {
  type: 'heading';
  /** Where the words come from: a section's title, your own text, or the board's title/brand. */
  bind: (typeof BLOCK_HEADING_BINDS)[number];
  sectionId: string | null;
  text: string;
  textAr: string | null;
  font: DesignFontRole;
  size: number;
  color: DesignColor;
  align: BlockAlign;
  uppercase: boolean;
  ornament: (typeof BLOCK_ORNAMENTS)[number];
  ornamentColor: DesignColor;
}

export interface ListBlock extends BlockBase {
  type: 'list';
  /** A section, or null for every section in turn, each under its heading. */
  sectionId: string | null;
  showHeading: boolean;
  innerColumns: number;
  leader: (typeof BLOCK_LEADERS)[number];
  showDescriptions: boolean;
  showSizes: boolean;
  font: DesignFontRole;
  nameSize: number;
  headingSize: number;
  nameColor: DesignColor;
  priceColor: DesignColor;
  textColor: DesignColor;
  headingColor: DesignColor;
}

export interface ImageBlock extends BlockBase {
  type: 'image';
  /** A picture from the media library; used when `itemId` is null. */
  mediaId: string | null;
  /** Or the photo of an item on the board. */
  itemId: string | null;
  fit: 'cover' | 'contain';
}

export interface TextBlock extends BlockBase {
  type: 'text';
  text: string;
  textAr: string | null;
  font: DesignFontRole;
  size: number;
  weight: number;
  color: DesignColor;
  align: BlockAlign;
}

export interface BadgeBlock extends BlockBase {
  type: 'badge';
  text: string;
  textAr: string | null;
  shape: (typeof BLOCK_BADGE_SHAPES)[number];
  fill: DesignColor;
  textColor: DesignColor;
  size: number;
  font: DesignFontRole;
  rotation: number;
}

export interface SpacerBlock extends BlockBase {
  type: 'spacer';
}

export type Block = ItemBlock | HeadingBlock | ListBlock | ImageBlock | TextBlock | BadgeBlock | SpacerBlock;

export interface BlocksLayout {
  version: number;
  /** Columns on a landscape screen, 1–6. */
  columns: number;
  /** Columns on a portrait screen, 1–6; the same blocks reflow into them. */
  portraitColumns: number;
  /** A fixed number of rows (blocks that do not fit are left out), or null for as many as needed. */
  rows: number | null;
  portraitRows: number | null;
  gap: number;
  padding: number;
  /** Over the board's own background: null colour keeps the Style tab's colour and picture. */
  background: { color: DesignColor | null; mediaId: string | null; overlay: number };
  blocks: Block[];
}

/* ----------------------------------------------------------------- field specs */

/**
 * What each field may hold. One table drives both the renderer's sanitiser (clamp and fall back
 * to the default) and the API's validator (refuse), so the two never disagree on a bound.
 */
export type BlockFieldSpec =
  | { t: 'num'; min: number; max: number; int?: boolean }
  | { t: 'enum'; values: readonly string[] }
  | { t: 'bool' }
  | { t: 'color'; nullable?: boolean }
  | { t: 'text'; max: number; nullable?: boolean }
  | { t: 'ref'; nullable?: boolean }
  | { t: 'media'; nullable?: boolean };

type Specs = Record<string, BlockFieldSpec>;

const font: BlockFieldSpec = { t: 'enum', values: BLOCK_FONTS };
const color: BlockFieldSpec = { t: 'color' };
const align: BlockFieldSpec = { t: 'enum', values: BLOCK_ALIGNS };
const bool: BlockFieldSpec = { t: 'bool' };

export const BLOCK_BOX_SPEC: Specs = {
  background: { t: 'color', nullable: true },
  borderColor: color,
  borderWidth: { t: 'num', min: 0, max: 24 },
  radius: { t: 'num', min: 0, max: 400 },
  padding: { t: 'num', min: 0, max: 120 },
  shadow: bool,
};

export const BLOCK_SPECS: Record<BlockType, Specs> = {
  item: {
    photo: { t: 'enum', values: BLOCK_PHOTO_POSITIONS },
    photoShape: { t: 'enum', values: BLOCK_PHOTO_SHAPES },
    photoSize: { t: 'num', min: 15, max: 85 },
    overlay: { t: 'num', min: 0, max: 0.95 },
    showName: bool,
    showDescription: bool,
    showPrice: bool,
    showSizes: bool,
    showBadges: bool,
    font,
    nameSize: { t: 'num', min: 10, max: 160 },
    textSize: { t: 'num', min: 8, max: 80 },
    priceSize: { t: 'num', min: 10, max: 160 },
    nameColor: color,
    textColor: color,
    priceColor: color,
    priceStyle: { t: 'enum', values: BLOCK_PRICE_STYLES },
    priceFill: color,
    align,
  },
  heading: {
    bind: { t: 'enum', values: BLOCK_HEADING_BINDS },
    sectionId: { t: 'ref', nullable: true },
    text: { t: 'text', max: 200 },
    textAr: { t: 'text', max: 200, nullable: true },
    font,
    size: { t: 'num', min: 12, max: 240 },
    color,
    align,
    uppercase: bool,
    ornament: { t: 'enum', values: BLOCK_ORNAMENTS },
    ornamentColor: color,
  },
  list: {
    sectionId: { t: 'ref', nullable: true },
    showHeading: bool,
    innerColumns: { t: 'num', min: 1, max: 3, int: true },
    leader: { t: 'enum', values: BLOCK_LEADERS },
    showDescriptions: bool,
    showSizes: bool,
    font,
    nameSize: { t: 'num', min: 10, max: 100 },
    headingSize: { t: 'num', min: 12, max: 160 },
    nameColor: color,
    priceColor: color,
    textColor: color,
    headingColor: color,
  },
  image: {
    mediaId: { t: 'media', nullable: true },
    itemId: { t: 'ref', nullable: true },
    fit: { t: 'enum', values: ['cover', 'contain'] },
  },
  text: {
    text: { t: 'text', max: 600 },
    textAr: { t: 'text', max: 600, nullable: true },
    font,
    size: { t: 'num', min: 8, max: 200 },
    weight: { t: 'num', min: 100, max: 900, int: true },
    color,
    align,
  },
  badge: {
    text: { t: 'text', max: 60 },
    textAr: { t: 'text', max: 60, nullable: true },
    shape: { t: 'enum', values: BLOCK_BADGE_SHAPES },
    fill: color,
    textColor: color,
    size: { t: 'num', min: 10, max: 200 },
    font,
    rotation: { t: 'num', min: -30, max: 30 },
  },
  spacer: {},
};

/* -------------------------------------------------------------------- defaults */

export const DEFAULT_BLOCK_BOX: BlockBox = {
  background: 'theme:surface',
  borderColor: 'theme:accent',
  borderWidth: 0,
  radius: 18,
  padding: 18,
  shadow: false,
};

const NO_BOX: BlockBox = { ...DEFAULT_BLOCK_BOX, background: null, padding: 8 };

/** Every field of a new block of each type; the sanitiser falls back to these too. */
export const BLOCK_DEFAULTS: { [K in BlockType]: Omit<Extract<Block, { type: K }>, 'id'> } = {
  item: {
    type: 'item',
    colSpan: 1,
    rowSpan: 1,
    box: DEFAULT_BLOCK_BOX,
    source: { kind: 'next', sectionId: null },
    photo: 'top',
    photoShape: 'rounded',
    photoSize: 55,
    overlay: 0.55,
    showName: true,
    showDescription: true,
    showPrice: true,
    showSizes: true,
    showBadges: true,
    font: 'heading',
    nameSize: 34,
    textSize: 18,
    priceSize: 34,
    nameColor: 'theme:primary',
    textColor: 'theme:text',
    priceColor: 'theme:accent',
    priceStyle: 'plain',
    priceFill: 'theme:accent',
    align: 'start',
  },
  heading: {
    type: 'heading',
    colSpan: 6,
    rowSpan: 1,
    box: NO_BOX,
    bind: 'section',
    sectionId: null,
    text: '',
    textAr: null,
    font: 'heading',
    size: 56,
    color: 'theme:primary',
    align: 'center',
    uppercase: true,
    ornament: 'lines',
    ornamentColor: 'theme:accent',
  },
  list: {
    type: 'list',
    colSpan: 2,
    rowSpan: 1,
    box: DEFAULT_BLOCK_BOX,
    sectionId: null,
    showHeading: true,
    innerColumns: 1,
    leader: 'dotted',
    showDescriptions: true,
    showSizes: true,
    font: 'heading',
    nameSize: 28,
    headingSize: 40,
    nameColor: 'theme:primary',
    priceColor: 'theme:accent',
    textColor: 'theme:text',
    headingColor: 'theme:accent',
  },
  image: {
    type: 'image',
    colSpan: 1,
    rowSpan: 1,
    box: { ...DEFAULT_BLOCK_BOX, background: null, padding: 0 },
    mediaId: null,
    itemId: null,
    fit: 'cover',
  },
  text: {
    type: 'text',
    colSpan: 2,
    rowSpan: 1,
    box: NO_BOX,
    text: 'Your text here',
    textAr: null,
    font: 'body',
    size: 28,
    weight: 400,
    color: 'theme:text',
    align: 'center',
  },
  badge: {
    type: 'badge',
    colSpan: 1,
    rowSpan: 1,
    box: { ...NO_BOX, padding: 0 },
    text: 'Make it a combo',
    textAr: 'اجعلها وجبة',
    shape: 'burst',
    fill: 'theme:accent',
    textColor: 'theme:bg',
    size: 34,
    font: 'heading',
    rotation: -8,
  },
  spacer: {
    type: 'spacer',
    colSpan: 1,
    rowSpan: 1,
    box: { ...NO_BOX, padding: 0 },
  },
};

/** A new block of a type with its defaults and any overrides (box fields merge). */
export function makeBlock<K extends BlockType>(
  type: K,
  id: string,
  overrides: Partial<Omit<Extract<Block, { type: K }>, 'box'>> & { box?: Partial<BlockBox> } = {},
): Extract<Block, { type: K }> {
  const defaults = BLOCK_DEFAULTS[type] as unknown as Record<string, unknown>;
  const { box, ...rest } = overrides as { box?: Partial<BlockBox> } & Record<string, unknown>;
  return {
    ...structuredCloneSafe(defaults),
    ...rest,
    id,
    type,
    box: { ...(defaults.box as BlockBox), ...box },
  } as unknown as Extract<Block, { type: K }>;
}

function structuredCloneSafe<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/* ------------------------------------------------------------------ sanitising */

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const TOKEN = /^theme:(bg|surface|text|primary|accent)$/;

export function isDesignColor(value: unknown): value is string {
  return typeof value === 'string' && (HEX.test(value) || TOKEN.test(value));
}

function cleanField(spec: BlockFieldSpec, value: unknown, fallback: unknown): unknown {
  switch (spec.t) {
    case 'num': {
      if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
      const n = Math.min(spec.max, Math.max(spec.min, value));
      return spec.int ? Math.round(n) : n;
    }
    case 'enum':
      return typeof value === 'string' && spec.values.includes(value) ? value : fallback;
    case 'bool':
      return typeof value === 'boolean' ? value : fallback;
    case 'color':
      if (value === null && spec.nullable) return null;
      return isDesignColor(value) ? value : fallback;
    case 'text':
      if (value === null && spec.nullable) return null;
      return typeof value === 'string' ? value.slice(0, spec.max) : fallback;
    case 'ref':
    case 'media':
      if (value === null && spec.nullable) return null;
      return typeof value === 'string' && value.length > 0 && value.length <= 64 ? value : fallback;
    default:
      return fallback;
  }
}

function cleanFields(specs: Specs, value: Record<string, unknown>, defaults: Record<string, unknown>) {
  const out: Record<string, unknown> = {};
  for (const [key, spec] of Object.entries(specs)) out[key] = cleanField(spec, value[key], defaults[key]);
  return out;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function cleanSource(value: unknown): BlockItemSource {
  if (isObject(value) && value.kind === 'item' && typeof value.itemId === 'string' && value.itemId) {
    return { kind: 'item', itemId: value.itemId.slice(0, 64) };
  }
  if (isObject(value) && value.kind === 'next') {
    const sectionId = typeof value.sectionId === 'string' && value.sectionId ? value.sectionId.slice(0, 64) : null;
    return { kind: 'next', sectionId };
  }
  return { kind: 'next', sectionId: null };
}

/** A block as it can be drawn, or null for a type this release does not know. */
export function sanitizeBlock(value: unknown, index = 0): Block | null {
  if (!isObject(value) || typeof value.type !== 'string' || !(BLOCK_TYPES as readonly string[]).includes(value.type)) {
    return null;
  }
  const type = value.type as BlockType;
  const defaults = BLOCK_DEFAULTS[type] as unknown as Record<string, unknown>;
  const block: Record<string, unknown> = {
    id: typeof value.id === 'string' && value.id ? value.id.slice(0, 40) : `b${index}`,
    type,
    colSpan: cleanField({ t: 'num', min: 1, max: BLOCKS_MAX_COLUMNS, int: true }, value.colSpan, 1),
    rowSpan: cleanField({ t: 'num', min: 1, max: BLOCKS_MAX_SPAN_ROWS, int: true }, value.rowSpan, 1),
    box: cleanFields(BLOCK_BOX_SPEC, isObject(value.box) ? value.box : {}, defaults.box as Record<string, unknown>),
    ...cleanFields(BLOCK_SPECS[type], value, defaults),
  };
  if (type === 'item') block.source = cleanSource(value.source);
  return block as unknown as Block;
}

/**
 * Any value as a layout the renderer can draw: numbers clamped, colours checked, unknown blocks
 * dropped, missing fields defaulted. Null when it is not a layout at all.
 */
export function sanitizeBlocksLayout(value: unknown): BlocksLayout | null {
  if (!isObject(value) || !Array.isArray(value.blocks)) return null;
  const cols = { t: 'num', min: 1, max: BLOCKS_MAX_COLUMNS, int: true } as const;
  const rows = (v: unknown) => (v === null || v === undefined ? null : (cleanField({ t: 'num', min: 1, max: BLOCKS_MAX_ROWS, int: true }, v, null) as number | null));
  const bg = isObject(value.background) ? value.background : {};
  const columns = cleanField(cols, value.columns, 3) as number;
  const seen = new Set<string>();
  const blocks: Block[] = [];
  for (const [i, raw] of value.blocks.slice(0, BLOCKS_MAX).entries()) {
    const block = sanitizeBlock(raw, i);
    if (!block) continue;
    // Ids are how the editor and the page cut tell blocks apart; keep them unique.
    if (seen.has(block.id)) block.id = `${block.id}-${i}`;
    seen.add(block.id);
    blocks.push(block);
  }
  return {
    version: typeof value.version === 'number' ? value.version : BLOCKS_LAYOUT_VERSION,
    columns,
    portraitColumns: cleanField(cols, value.portraitColumns, Math.min(columns, 2)) as number,
    rows: rows(value.rows),
    portraitRows: rows(value.portraitRows),
    gap: cleanField({ t: 'num', min: 0, max: 120 }, value.gap, 20) as number,
    padding: cleanField({ t: 'num', min: 0, max: 200 }, value.padding, 40) as number,
    background: {
      color: cleanField({ t: 'color', nullable: true }, bg.color ?? null, null) as string | null,
      mediaId: cleanField({ t: 'media', nullable: true }, bg.mediaId ?? null, null) as string | null,
      overlay: cleanField({ t: 'num', min: 0, max: 0.9 }, bg.overlay, 0) as number,
    },
    blocks,
  };
}

/* ------------------------------------------------------------ board and media */

/**
 * The layout a board is drawn with. Manifests carry it inside the `custom` field (see
 * `blocksEnvelope`), because players parse manifests with the schema they were built with and
 * would drop a field they do not know, while `custom` reaches them untouched.
 */
export function boardBlocks(board: Pick<MenuBoardContent, 'blocks' | 'custom'>): BlocksLayout | null {
  const envelope = board.custom as (CustomDesign & { blocks?: unknown }) | null | undefined;
  return sanitizeBlocksLayout(board.blocks ?? envelope?.blocks ?? null);
}

/** Every library picture a layout shows (item photos come with the board's items). */
export function blocksMediaIds(layout: BlocksLayout | null | undefined): string[] {
  if (!layout) return [];
  const ids = [layout.background?.mediaId];
  for (const block of layout.blocks ?? []) if (block.type === 'image' && !block.itemId) ids.push(block.mediaId);
  return ids.filter((id): id is string => Boolean(id));
}

/**
 * A layout wrapped as a custom design for the manifest's `custom` field: a design without
 * elements to draw, whose background and image list name the layout's pictures, so players
 * released before this layout existed still download them (they find a board's pictures
 * through `custom`). It is never drawn as a design, because the board's template is `blocks`.
 */
export function blocksEnvelope(layout: BlocksLayout): CustomDesign & { blocks: BlocksLayout } {
  const pictures = blocksMediaIds({ ...layout, background: { ...layout.background, mediaId: null } });
  return {
    width: 1920,
    height: 1080,
    background: { color: 'theme:bg', mediaId: layout.background.mediaId, overlay: 0 },
    elements: pictures.map((mediaId, i) => ({
      id: `blocks-media-${i}`,
      type: 'image' as const,
      mediaId,
      source: null,
      fit: 'cover' as const,
      radius: 0,
      borderWidth: 0,
      borderColor: 'theme:bg',
      shadow: false,
      x: 0,
      y: 0,
      w: 1,
      h: 1,
      rotation: 0,
      opacity: 0,
    })),
    blocks: layout,
  };
}

/* ---------------------------------------------------------------- placement */

export interface BlockPlacement {
  /** Index into `layout.blocks`. */
  index: number;
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
}

export interface BlocksGrid {
  columns: number;
  rows: number;
  placements: BlockPlacement[];
  /** Blocks left out because a fixed row count has no room for them. */
  hidden: number[];
  /** Per row: a fixed height in reference pixels for rows holding only headings and texts, or null to share what is left. */
  rowHeights: (number | null)[];
  /** The screen at reference size, and the height each shared row gets on it. */
  width: number;
  height: number;
  frRowHeight: number;
  colWidth: number;
}

const COMPACT: BlockType[] = ['heading', 'text'];

/**
 * Where each block sits: in order, row by row, each in the first free spot after the previous
 * one (CSS grid's sparse auto-placement), spans narrowed to the grid.
 */
export function blocksGrid(layout: BlocksLayout, orientation: MenuOrientation, board?: MenuBoardContent): BlocksGrid {
  const portrait = orientation === 'portrait';
  const columns = portrait ? layout.portraitColumns : layout.columns;
  const fixedRows = portrait ? layout.portraitRows : layout.rows;
  const maxRows = fixedRows ?? BLOCKS_MAX_ROWS * 2;
  const taken: boolean[][] = [];
  const free = (r: number, c: number, cs: number, rs: number) => {
    for (let y = r; y < r + rs; y += 1) for (let x = c; x < c + cs; x += 1) if (taken[y]?.[x]) return false;
    return true;
  };
  const placements: BlockPlacement[] = [];
  const hidden: number[] = [];
  let cursorRow = 0;
  let cursorCol = 0;
  layout.blocks.forEach((block, index) => {
    const cs = Math.min(columns, Math.max(1, block.colSpan));
    const rs = Math.min(fixedRows ?? BLOCKS_MAX_SPAN_ROWS, Math.max(1, block.rowSpan));
    let spot: [number, number] | null = null;
    for (let r = cursorRow; r + rs <= maxRows && !spot; r += 1) {
      for (let c = r === cursorRow ? cursorCol : 0; c + cs <= columns; c += 1) {
        if (free(r, c, cs, rs)) {
          spot = [r, c];
          break;
        }
      }
    }
    if (!spot) {
      hidden.push(index);
      return;
    }
    const [row, col] = spot;
    for (let y = row; y < row + rs; y += 1) {
      taken[y] ??= [];
      for (let x = col; x < col + cs; x += 1) taken[y]![x] = true;
    }
    placements.push({ index, col, row, colSpan: cs, rowSpan: rs });
    cursorRow = row;
    cursorCol = col + cs;
  });
  const rows = fixedRows ?? Math.max(1, ...placements.map((p) => p.row + p.rowSpan));

  const [width, height] = portrait ? [1080, 1920] : [1920, 1080];
  const pad = layout.padding;
  const gap = layout.gap;
  const colWidth = (width - 2 * pad - gap * (columns - 1)) / columns;

  // A row holding only one-row headings and texts is as tall as they need; the rest share.
  const rowHeights: (number | null)[] = Array.from({ length: rows }, () => null);
  for (let r = 0; r < rows; r += 1) {
    const here = placements.filter((p) => p.row <= r && r < p.row + p.rowSpan);
    if (here.length === 0) continue;
    if (!here.every((p) => p.rowSpan === 1 && COMPACT.includes(layout.blocks[p.index]!.type))) continue;
    rowHeights[r] = Math.max(
      ...here.map((p) => compactHeight(layout.blocks[p.index]!, colWidth * p.colSpan + gap * (p.colSpan - 1), board)),
    );
  }
  const fixed = rowHeights.reduce<number>((sum, h) => sum + (h ?? 0), 0);
  const shared = rowHeights.filter((h) => h === null).length;
  const frRowHeight = shared ? Math.max(40, (height - 2 * pad - gap * (rows - 1) - fixed) / shared) : 0;
  return { columns, rows, placements, hidden, rowHeights, width, height, frRowHeight, colWidth };
}

/** The reference-pixel size of a placed block's inside (within its frame). */
export function blockInnerSize(grid: BlocksGrid, layout: BlocksLayout, p: BlockPlacement): { w: number; h: number } {
  const block = layout.blocks[p.index]!;
  const inset = 2 * (block.box.padding + block.box.borderWidth);
  const w = grid.colWidth * p.colSpan + layout.gap * (p.colSpan - 1) - inset;
  let h = layout.gap * (p.rowSpan - 1);
  for (let r = p.row; r < p.row + p.rowSpan; r += 1) h += grid.rowHeights[r] ?? grid.frRowHeight;
  return { w: Math.max(10, w), h: Math.max(10, h - inset) };
}

type Langs = { en: boolean; ar: boolean };
const langsOf = (board?: Pick<MenuBoardContent, 'settings'>): Langs => {
  const language = board?.settings.language ?? 'en';
  return { en: language !== 'ar', ar: language !== 'en' };
};

/** Lines a text takes in the board's language(s): Arabic lines are set taller. */
function textLines(en: string | null | undefined, ar: string | null | undefined, langs: Langs, perLine: number, max: number) {
  const english = langs.en || !ar?.trim() ? lineCount(en, perLine, max) : 0;
  const arabic = langs.ar ? lineCount(ar, perLine, max) * 1.25 * (langs.en && english ? 0.8 : 1) : 0;
  return Math.max(english + arabic, 1);
}

function headingWords(block: HeadingBlock, board?: MenuBoardContent): [string | null, string | null] {
  if (!board) return [block.text, block.textAr];
  switch (block.bind) {
    case 'section': {
      const section = sectionById(board, block.sectionId);
      return [section?.title ?? null, section?.titleAr ?? null];
    }
    case 'title':
      return [board.settings.title ?? board.name, board.settings.titleAr];
    case 'brand':
      return [board.brand?.name ?? null, board.brand?.nameAr ?? null];
    case 'tagline':
      return [board.brand?.tagline ?? null, board.brand?.taglineAr ?? null];
    default:
      return [block.text, block.textAr];
  }
}

function compactHeight(block: Block, width: number, board?: MenuBoardContent): number {
  const langs = langsOf(board);
  const frame = 2 * (block.box.padding + block.box.borderWidth);
  const inner = Math.max(40, width - frame);
  if (block.type === 'heading') {
    const [en, ar] = headingWords(block, board);
    const lines = textLines(en, ar, langs, inner / (block.size * 0.62), 2);
    const ornament = block.ornament === 'underline' || block.ornament === 'dots' ? block.size * 0.45 : 0;
    return Math.ceil(lines * block.size * 1.2 + ornament + frame + 6);
  }
  if (block.type === 'text') {
    const lines = textLines(block.text, block.textAr, langs, inner / (block.size * 0.5), 6);
    return Math.ceil(lines * block.size * 1.35 + frame + 6);
  }
  return 80;
}

/* ---------------------------------------------------------------- page cut */

function sectionById(board: Pick<MenuBoardContent, 'sections'>, id: string | null | undefined): MenuSection | null {
  if (!id) return null;
  return board.sections?.find((s) => s.id === id) ?? null;
}

const UNTITLED: MenuSection = { id: '', title: null, titleAr: null, subtitle: null, subtitleAr: null, imageMediaId: null };

/** The height of one list line in reference pixels, as the list's CSS sets it. */
function listLineHeight(block: ListBlock, item: MenuItemContent, langs: Langs, width: number, showDescriptions: boolean) {
  const n = block.nameSize;
  const desc = n * 0.6;
  const english = langs.en || !item.titleAr?.trim();
  let h = n * 1.18 * (english ? lineCount(item.title, width / (n * 0.55), 2) || 1 : 1.25);
  if (langs.en && langs.ar && item.titleAr?.trim()) h += n * 0.78 * 1.3;
  if (showDescriptions && block.showDescriptions) {
    const perLine = width / (desc * 0.5);
    const lines =
      (english ? lineCount(item.description, perLine, 2) : 0) + (langs.ar ? lineCount(item.descriptionAr, perLine, 2) * 1.25 : 0);
    if (lines) h += lines * desc * 1.3 + desc * 0.2;
  }
  return h + n * 0.5;
}

function listHeadingHeight(block: ListBlock, section: MenuSection, langs: Langs) {
  if (!block.showHeading || !(section.title?.trim() || section.titleAr?.trim())) return 0;
  const both = langs.en && langs.ar && section.title?.trim() && section.titleAr?.trim();
  return block.headingSize * (both ? 2.05 : 1.25) + block.headingSize * 0.35;
}

/**
 * The screens a layout fills: on each, every block takes its items in order (chosen items stay
 * on every page; "next item" cards and lists take what is not shown yet). Another page follows
 * while items are left for them. `columns[i]` of a page is what block i shows on it.
 */
export function paginateBlocks(board: MenuBoardContent, orientation: MenuOrientation): MenuPage[] {
  const layout = boardBlocks(board) ?? defaultBlocksLayout();
  const grid = blocksGrid(layout, orientation, board);
  const langs = langsOf(board);
  const groups = groupBySection(board);
  const sectionOfItem = new Map<string, MenuSection>();
  for (const g of groups) for (const item of g.items) sectionOfItem.set(item.id, g.section);
  const ordered = groups.flatMap((g) => g.items);
  const known = new Set(ordered.map((i) => i.id));
  // Items a card shows by name are not handed out again by lists and "next" cards.
  const pinned = new Set<string>();
  for (const p of grid.placements) {
    const b = layout.blocks[p.index]!;
    if (b.type === 'item' && b.source.kind === 'item' && known.has(b.source.itemId)) pinned.add(b.source.itemId);
  }
  const used = new Set<string>();
  const pool = (sectionId: string | null) =>
    ordered.filter(
      (i) => !used.has(i.id) && !pinned.has(i.id) && (sectionId === null || sectionOfItem.get(i.id)?.id === sectionId),
    );
  const consumers = grid.placements.filter((p) => {
    const b = layout.blocks[p.index]!;
    return (b.type === 'item' && b.source.kind === 'next') || b.type === 'list';
  });
  const poolOf = (p: BlockPlacement) => {
    const b = layout.blocks[p.index]!;
    return b.type === 'item' && b.source.kind === 'next' ? b.source.sectionId : b.type === 'list' ? b.sectionId : null;
  };

  const pages: MenuPage[] = [];
  for (let n = 0; n < MAX_PAGES; n += 1) {
    const columns: MenuBlock[][] = layout.blocks.map(() => []);
    let taken = 0;
    for (const p of grid.placements) {
      const block = layout.blocks[p.index]!;
      if (block.type === 'item') {
        let item: MenuItemContent | undefined;
        if (block.source.kind === 'item') {
          const id = block.source.itemId;
          item = board.items.find((i) => i.id === id);
        } else {
          item = pool(block.source.sectionId)[0];
          if (item) {
            used.add(item.id);
            taken += 1;
          }
        }
        if (item) columns[p.index] = [{ section: sectionOfItem.get(item.id) ?? UNTITLED, items: [item], continued: false }];
      } else if (block.type === 'list') {
        const { w, h } = blockInnerSize(grid, layout, p);
        const cols = block.innerColumns;
        const colW = (w - (cols - 1) * block.nameSize * 1.4) / cols;
        const room = h * 0.94;
        const out: MenuBlock[] = [];
        let col = 0;
        let filled = 0;
        let current: MenuBlock | null = null;
        for (const item of pool(block.sectionId)) {
          const section = sectionOfItem.get(item.id) ?? UNTITLED;
          const opens: boolean = !current || current.section.id !== section.id;
          // A section that started on an earlier page carries on under its heading again.
          const continued: boolean = opens && n > 0 && ordered.some((o) => used.has(o.id) && sectionOfItem.get(o.id)?.id === section.id);
          const cost =
            listLineHeight(block, item, langs, colW, board.settings.showDescriptions) +
            (opens ? listHeadingHeight(block, section, langs) : 0);
          if (filled + cost > room && (filled > 0 || col > 0 || out.length > 0)) {
            col += 1;
            filled = 0;
            if (col >= cols) break;
          }
          if (opens) {
            current = { section, items: [], continued };
            out.push(current);
          }
          current!.items.push(item);
          filled += cost;
        }
        for (const b of out) for (const item of b.items) used.add(item.id);
        taken += out.reduce((sum, b) => sum + b.items.length, 0);
        columns[p.index] = out;
      }
    }
    if (n > 0 && taken === 0) break;
    pages.push({ columns });
    if (!consumers.some((p) => pool(poolOf(p)).length > 0)) break;
  }
  return pages;
}

/* -------------------------------------------------------------------- drawing */

const S = '.mb-t-blocks';
const px = (value: number) => `${Math.round((value / 16) * 1000) / 1000}em`;

function boxStyle(box: BlockBox): string[] {
  return [
    box.background ? `background:${designColor(box.background)}` : '',
    box.borderWidth ? `border:${px(box.borderWidth)} solid ${designColor(box.borderColor, 'var(--mb-accent)')}` : '',
    box.radius ? `border-radius:${px(box.radius)}` : '',
    `padding:${px(box.padding)}`,
    box.shadow ? 'box-shadow:0 .6em 1.6em rgba(0,0,0,.35)' : '',
  ];
}

function cell(block: Block, p: BlockPlacement, cls: string, style: string[], inner: string, withBox = true): string {
  const position = [`grid-column:${p.col + 1} / span ${p.colSpan}`, `grid-row:${p.row + 1} / span ${p.rowSpan}`];
  const all = [...position, ...(withBox ? boxStyle(block.box) : []), ...style].filter(Boolean).join(';');
  return `<div class="bk-b ${cls}" data-block="${escapeHtml(block.id)}" style="${escapeHtml(all)}">${inner}</div>`;
}

const fontOf = (role: DesignFontRole) => FONT_VARS[role] ?? FONT_VARS.heading;

function plainText(ctx: RenderContext, en: string | null | undefined, ar: string | null | undefined, cls: string): string {
  return bilingual(ctx, en, ar, cls);
}

function itemCard(ctx: RenderContext, block: ItemBlock, p: BlockPlacement, entries: MenuBlock[]): string {
  const item = entries[0]?.items[0];
  if (!item) return cell(block, p, 'bk-void', [], '', false);
  const shown = block.showSizes ? item : { ...item, variants: [] };
  const photoUrl = block.photo === 'none' ? null : safeUrl(ctx.img(item.imageMediaId));
  const position = photoUrl ? block.photo : 'none';
  const badges = block.showBadges ? badge(ctx, item, 'bk-chip') : '';
  const price = block.showPrice ? priceBlock(ctx, shown, 'bp') : '';
  const text = [
    badges ? `<div class="bk-chips">${badges}</div>` : '',
    block.showName ? plainText(ctx, item.title, item.titleAr, 'bk-name') : '',
    block.showDescription ? description(ctx, item, 'bk-desc') : '',
    price ? `<div class="bk-price bk-pr-${block.priceStyle}${shown.variants.length ? ' bk-pr-sizes' : ''}">${price}</div>` : '',
  ].join('');
  const photo = photoUrl
    ? position === 'background'
      ? `<img class="bk-cover" src="${escapeHtml(photoUrl)}" alt=""><div class="bk-shade" style="opacity:${block.overlay}"></div>`
      : `<div class="bk-photo"><img src="${escapeHtml(photoUrl)}" alt=""></div>`
    : '';
  return cell(
    block,
    p,
    `bk-card bk-ph-${position} bk-shape-${block.photoShape} bk-al-${block.align}`,
    [
      `--bk-photo:${block.photoSize}%`,
      `--bk-name:${px(block.nameSize)}`,
      `--bk-text:${px(block.textSize)}`,
      `--bk-psize:${px(block.priceSize)}`,
      `--bk-nc:${designColor(block.nameColor, 'var(--mb-primary)')}`,
      `--bk-tc:${designColor(block.textColor, 'var(--mb-text)')}`,
      // On a pill or circle in its own colour the price would vanish; it takes the background's.
      `--bk-pc:${block.priceStyle !== 'plain' && block.priceColor === block.priceFill ? 'var(--mb-bg)' : designColor(block.priceColor, 'var(--mb-accent)')}`,
      `--bk-pf:${designColor(block.priceFill, 'var(--mb-accent)')}`,
      `--bk-font:${fontOf(block.font)}`,
    ],
    `${photo}<div class="bk-text">${text}</div>`,
  );
}

function heading(ctx: RenderContext, block: HeadingBlock, p: BlockPlacement): string {
  const [en, ar] = headingWords(block, ctx.board);
  return cell(
    block,
    p,
    `bk-heading bk-orn-${block.ornament} bk-al-${block.align}${block.uppercase ? ' bk-upper' : ''}`,
    [
      `--bk-hs:${px(block.size)}`,
      `--bk-hc:${designColor(block.color, 'var(--mb-primary)')}`,
      `--bk-oc:${designColor(block.ornamentColor, 'var(--mb-accent)')}`,
      `--bk-font:${fontOf(block.font)}`,
    ],
    `<div class="bk-h">${plainText(ctx, en, ar, 'bk-h-t')}</div>`,
  );
}

function list(ctx: RenderContext, block: ListBlock, p: BlockPlacement, entries: MenuBlock[]): string {
  const body = entries
    .map((entry) => {
      const title =
        block.showHeading && (entry.section.title?.trim() || entry.section.titleAr?.trim())
          ? `<div class="bk-lh">${plainText(ctx, entry.section.title, entry.section.titleAr, 'bk-lh-t')}</div>`
          : '';
      const items = entry.items
        .map((item) => listItem(ctx, block.showSizes ? item : { ...item, variants: [] }, 'bl', block.leader !== 'none'))
        .join('');
      return title + items;
    })
    .join('');
  return cell(
    block,
    p,
    `bk-list bk-leader-${block.leader}${block.showDescriptions ? '' : ' bk-nodesc'}`,
    [
      `font-size:${px(block.nameSize)}`,
      `--bk-cols:${block.innerColumns}`,
      `--bk-hsr:${Math.round((block.headingSize / block.nameSize) * 1000) / 1000}em`,
      `--bk-nc:${designColor(block.nameColor, 'var(--mb-primary)')}`,
      `--bk-pc:${designColor(block.priceColor, 'var(--mb-accent)')}`,
      `--bk-tc:${designColor(block.textColor, 'var(--mb-text)')}`,
      `--bk-hc:${designColor(block.headingColor, 'var(--mb-accent)')}`,
      `--bk-font:${fontOf(block.font)}`,
    ],
    `<div class="bk-lbody">${body}</div>`,
  );
}

function imageBlock(ctx: RenderContext, block: ImageBlock, p: BlockPlacement): string {
  const mediaId = block.itemId ? ctx.board.items.find((i) => i.id === block.itemId)?.imageMediaId : block.mediaId;
  const url = safeUrl(ctx.img(mediaId));
  const inner = url
    ? `<img src="${escapeHtml(url)}" alt="" style="object-fit:${block.fit === 'contain' ? 'contain' : 'cover'}">`
    : '';
  return cell(block, p, 'bk-image', [block.box.radius ? `--bk-r:${px(Math.max(0, block.box.radius - block.box.padding))}` : ''], inner);
}

function textBlock(ctx: RenderContext, block: TextBlock, p: BlockPlacement): string {
  return cell(
    block,
    p,
    `bk-textb bk-al-${block.align}`,
    [
      `font-size:${px(block.size)}`,
      `font-weight:${block.weight}`,
      `color:${designColor(block.color, 'var(--mb-text)')}`,
      `font-family:${fontOf(block.font)}`,
    ],
    plainText(ctx, block.text, block.textAr, 'bk-tx'),
  );
}

function badgeBlock(ctx: RenderContext, block: BadgeBlock, p: BlockPlacement): string {
  const label = ctx.showEn ? block.text || block.textAr : block.textAr || block.text;
  const clip = block.shape === 'burst' || block.shape === 'ribbon' ? SHAPE_CLIP[block.shape] : undefined;
  const style = [
    `background:${designColor(block.fill, 'var(--mb-accent)')}`,
    `color:${designColor(block.textColor, 'var(--mb-bg)')}`,
    `font-size:${px(block.size)}`,
    `font-family:${fontOf(block.font)}`,
    block.rotation ? `transform:rotate(${block.rotation}deg)` : '',
    clip ? `clip-path:${clip}` : '',
  ]
    .filter(Boolean)
    .join(';');
  return cell(
    block,
    p,
    `bk-badge bk-bs-${block.shape}`,
    [],
    label?.trim() ? `<span class="bk-sticker" dir="auto" style="${escapeHtml(style)}">${escapeHtml(label.trim())}</span>` : '',
  );
}

/** One page of a layout: its background, then each placed block with what it shows here. */
export function renderBlocks(ctx: RenderContext): string {
  const layout = boardBlocks(ctx.board) ?? defaultBlocksLayout();
  const grid = blocksGrid(layout, ctx.orientation, ctx.board);
  const bg = layout.background;
  const photo = bg.mediaId ? safeUrl(ctx.img(bg.mediaId)) : null;
  const layers = [
    bg.color ? `<div class="bk-bgl" style="${escapeHtml(`background:${designColor(bg.color)}`)}"></div>` : '',
    photo ? `<img class="bk-bgl bk-bgimg" src="${escapeHtml(photo)}" alt="">` : '',
    bg.overlay ? `<div class="bk-bgl bk-bgshade" style="opacity:${bg.overlay}"></div>` : '',
  ].join('');
  const rows = grid.rowHeights.map((h) => (h === null ? 'minmax(0,1fr)' : px(h))).join(' ');
  const blocks = grid.placements
    .map((p) => {
      const block = layout.blocks[p.index]!;
      const entries = ctx.page.columns[p.index] ?? [];
      switch (block.type) {
        case 'item':
          return itemCard(ctx, block, p, entries);
        case 'heading':
          return heading(ctx, block, p);
        case 'list':
          return list(ctx, block, p, entries);
        case 'image':
          return imageBlock(ctx, block, p);
        case 'text':
          return textBlock(ctx, block, p);
        case 'badge':
          return badgeBlock(ctx, block, p);
        case 'spacer':
          return cell(block, p, 'bk-spacer', [], '');
        default:
          return '';
      }
    })
    .join('');
  const style = [
    `padding:${px(layout.padding)}`,
    `gap:${px(layout.gap)}`,
    `grid-template-columns:repeat(${grid.columns},minmax(0,1fr))`,
    `grid-template-rows:${rows}`,
  ].join(';');
  return `${layers}<div class="bk-grid" style="${escapeHtml(style)}">${blocks}</div>`;
}

export const BLOCKS_CSS = `
${S}{padding:0}
${S} .bk-bgl{position:absolute;inset:0;width:100%;height:100%;z-index:0}
${S} .bk-bgimg{object-fit:cover}
${S} .bk-bgshade{background:#000}
${S} .bk-grid{flex:1 1 0;min-height:0;display:grid;align-content:stretch}
${S} .bk-b{position:relative;min-width:0;min-height:0;overflow:hidden;display:flex}
${S} .bk-al-start{text-align:start;--bk-ai:flex-start}
${S} .bk-al-center{text-align:center;--bk-ai:center}
${S} .bk-al-end{text-align:end;--bk-ai:flex-end}

/* Item cards: photo beside, above, below or behind the text. "Left" is the reading side, so an
   Arabic board, drawn right to left, puts it on the right. */
${S} .bk-card{gap:.9em;color:var(--bk-tc)}
${S} .bk-ph-left{flex-direction:row}
${S} .bk-ph-right{flex-direction:row-reverse}
${S} .bk-ph-top{flex-direction:column}
${S} .bk-ph-bottom{flex-direction:column-reverse}
/* The text keeps the room it needs and the photo gives way, so a short card still shows the
   name and price; a photo beside the text keeps its width. */
${S} .bk-photo{position:relative;flex:0 1 var(--bk-photo);min-width:0;min-height:0;display:flex;align-items:center;justify-content:center;container-type:size}
${S} .bk-ph-left .bk-photo,${S} .bk-ph-right .bk-photo{flex-shrink:0}
${S} .bk-photo img{display:block;width:100%;height:100%;object-fit:cover}
${S} .bk-shape-rounded .bk-photo img{border-radius:.75em}
/* A circle is the largest square that fits, centred, however the card is shaped. */
${S} .bk-shape-circle .bk-photo img{width:min(100cqw,100cqh);height:min(100cqw,100cqh);border-radius:50%}
${S} .bk-cover{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0}
${S} .bk-shade{position:absolute;inset:0;z-index:0;background:linear-gradient(to top,#000 0%,rgba(0,0,0,.6) 45%,rgba(0,0,0,.15) 100%)}
${S} .bk-ph-background{flex-direction:column;justify-content:flex-end}
${S} .bk-ph-background .bk-text{flex:0 1 auto;position:relative;z-index:1}
${S} .bk-text{flex:1 1 0;min-width:0;min-height:0;max-height:100%;display:flex;flex-direction:column;justify-content:center;justify-content:safe center;align-items:var(--bk-ai);gap:.25em;overflow:hidden}
/* Short of room, the description gives way first, then the photo; the name, badges and price
   always stay whole. */
${S} .bk-text>*{flex:none;max-width:100%}
${S} .bk-text>.bk-desc{flex:0 1 auto;min-height:0}
${S} .bk-ph-top .bk-text,${S} .bk-ph-bottom .bk-text{flex:1 0 auto;max-height:82%}
${S} .bk-ph-top .bk-photo,${S} .bk-ph-bottom .bk-photo{min-height:18%}
${S} .bk-name{font-family:var(--bk-font);font-weight:var(--mb-heading-weight);font-size:var(--bk-name);color:var(--bk-nc);line-height:1.1;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .bk-name.mb-2nd{font-size:calc(var(--bk-name) * .78)}
${S} .bk-desc{font-size:var(--bk-text);opacity:.85;line-height:1.3;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
${S} .bk-desc.mb-2nd{-webkit-line-clamp:2}
${S} .bk-chips{display:flex;flex-wrap:wrap;gap:.3em;font-size:var(--bk-text)}
/* With a photo, badges sit on its corner and take no room from the text. */
${S} .bk-card:not(.bk-ph-none) .bk-chips{position:absolute;z-index:2;top:.6em;inset-inline-start:.6em;max-width:calc(100% - 1.2em)}
${S} .bk-chip{font-size:.8em;font-weight:800;padding:.15em .6em;border-radius:99em;background:var(--bk-pf);color:var(--mb-bg);white-space:nowrap}
${S} .bk-price{font-family:var(--bk-font);font-weight:var(--mb-heading-weight);font-size:var(--bk-psize);color:var(--bk-pc);line-height:1.1;margin-top:.15em}
${S} .bp-pricebox{display:inline-flex;align-items:baseline;gap:.35em}
${S} .bp-price,${S} .bp-old{white-space:nowrap}
${S} .bp-old{font-size:.6em;opacity:.6}
${S} .bp-sizes{display:flex;flex-wrap:wrap;gap:.25em .8em;justify-content:var(--bk-ai)}
${S} .bp-size{white-space:nowrap}
${S} .bp-size small{font-size:.55em;opacity:.8;margin-inline-end:.2em}
${S} .bk-pr-pill:not(.bk-pr-sizes){background:var(--bk-pf);padding:.18em .7em;border-radius:99em}
${S} .bk-pr-circle:not(.bk-pr-sizes){background:var(--bk-pf);border-radius:50%;min-width:3em;aspect-ratio:1;padding:.35em;flex:none;display:flex;align-items:center;justify-content:center;white-space:nowrap;font-size:calc(var(--bk-psize) * .6)}
${S} .bk-pr-circle .bp-pricebox{flex-direction:column;align-items:center;gap:0}
${S} .bk-pr-circle .bp-old{font-size:.5em}
/* Text never outgrows its card: names, descriptions and prices are capped at a share of the
   card's height and width (on engines with container units; others keep the set sizes). */
@supports (container-type:size){
${S} .bk-card{container-type:size}
${S} .bk-card .bk-name{font-size:min(var(--bk-name),13cqh,11cqw)}
${S} .bk-card .bk-name.mb-2nd{font-size:min(calc(var(--bk-name) * .78),10cqh,9cqw)}
${S} .bk-card .bk-desc,${S} .bk-card .bk-chips{font-size:min(var(--bk-text),7cqh,6cqw)}
${S} .bk-card .bk-price{font-size:min(var(--bk-psize),12cqh,11cqw)}
${S} .bk-card .bk-pr-circle:not(.bk-pr-sizes){font-size:min(calc(var(--bk-psize) * .6),7cqh,6.5cqw)}
@container (max-height:300px){${S} .bk-card .bk-desc{-webkit-line-clamp:1}}
}
${S} .bk-pr-pill.bk-pr-sizes .bp-size,${S} .bk-pr-circle.bk-pr-sizes .bp-size{background:var(--bk-pf);padding:.1em .55em;border-radius:99em}

/* Headings, with an optional ornament in the accent colour. */
${S} .bk-heading{flex-direction:column;justify-content:center;align-items:var(--bk-ai)}
${S} .bk-h{display:flex;flex-direction:column;align-items:var(--bk-ai);max-width:100%}
${S} .bk-h-t{font-family:var(--bk-font);font-weight:var(--mb-heading-weight);font-size:var(--bk-hs);color:var(--bk-hc);line-height:1.12}
${S} .bk-h-t.mb-2nd{font-size:calc(var(--bk-hs) * .62)}
${S} .bk-upper .bk-h-t{text-transform:uppercase;letter-spacing:.03em}
${S} .bk-orn-underline .bk-h::after{content:'';display:block;width:3.2em;height:.18em;border-radius:1em;background:var(--bk-oc);margin-top:.3em;font-size:var(--bk-hs)}
${S} .bk-orn-dots .bk-h::after{content:'\\2022\\2003\\2022\\2003\\2022';display:block;color:var(--bk-oc);font-size:calc(var(--bk-hs) * .4);margin-top:.25em}
${S} .bk-orn-lines{flex-direction:row;align-items:center;gap:calc(var(--bk-hs) * .35)}
${S} .bk-orn-lines::before,${S} .bk-orn-lines::after{content:'';flex:1 1 0;height:calc(var(--bk-hs) * .06);min-height:2px;background:var(--bk-oc)}
${S} .bk-orn-lines.bk-al-start::before,${S} .bk-orn-lines.bk-al-end::after{flex:0 0 calc(var(--bk-hs) * .4)}
${S} .bk-orn-lines .bk-h{flex:0 1 auto}

/* Lists flow down, then into the next inner column; a line that does not fit moves past the
   edge, where it is clipped, so a screen never shows half an item. */
${S} .bk-list{flex-direction:column;color:var(--bk-tc)}
${S} .bk-lbody{flex:1 1 0;min-height:0;display:flex;flex-direction:column;flex-wrap:wrap;align-content:flex-start;column-gap:1.4em;overflow:hidden}
${S} .bk-lbody>*{flex:none;width:calc((100% - (var(--bk-cols) - 1) * 1.4em) / var(--bk-cols))}
${S} .bk-lh{margin-bottom:.35em}
${S} .bk-lh-t{display:block;font-family:var(--bk-font);font-weight:var(--mb-heading-weight);color:var(--bk-hc);font-size:var(--bk-hsr);line-height:1.12;text-transform:uppercase}
${S} .bk-lh-t.mb-2nd{font-size:calc(var(--bk-hsr) * .7)}
${listCss(S, 'bl', { name: '1em', price: '1em', desc: '.6em', leader: '.08em dotted color-mix(in srgb,var(--bk-tc) 45%,transparent)' })}
${S} .bk-leader-dashed .bl-leader{border-bottom-style:dashed}
${S} .bl-item{margin-bottom:.5em}
${S} .bl-name{font-family:var(--bk-font);font-weight:var(--mb-heading-weight);color:var(--bk-nc)}
${S} .bl-name2{font-size:.78em;color:var(--bk-nc)}
${S} .bl-price{font-family:var(--bk-font);font-weight:var(--mb-heading-weight);color:var(--bk-pc)}
${S} .bl-badge{background:var(--bk-pc)}
${S} .bk-nodesc .bl-desc{display:none}

${S} .bk-image img{display:block;width:100%;height:100%;border-radius:var(--bk-r,0)}
${S} .bk-textb{flex-direction:column;justify-content:center;white-space:pre-wrap;overflow-wrap:anywhere;line-height:1.35}
${S} .bk-textb .mb-2nd{font-size:.85em;margin-top:.2em}
${S} .bk-badge{align-items:center;justify-content:center}
${S} .bk-sticker{display:flex;align-items:center;justify-content:center;text-align:center;font-weight:800;line-height:1.05;padding:.45em 1.1em;max-width:100%;max-height:100%;overflow:hidden}
${S} .bk-bs-pill .bk-sticker{border-radius:99em}
${S} .bk-bs-rect .bk-sticker{border-radius:.2em}
${S} .bk-bs-circle .bk-sticker,${S} .bk-bs-burst .bk-sticker{aspect-ratio:1;border-radius:50%;padding:.6em;width:min(100%,6.5em)}
${S} .bk-bs-burst .bk-sticker{border-radius:0;padding:1.3em}
${S} .bk-bs-ribbon .bk-sticker{padding:.45em 1.6em}
`;

/* ------------------------------------------------------- presets and layouts */

/** Ready-made blocks for the editor's palette: a type with a look. */
export const BLOCK_PRESETS = [
  'photo-left',
  'photo-right',
  'photo-top',
  'photo-full',
  'photo-round',
  'price-pill',
  'list',
  'list-bordered',
  'heading',
  'title',
  'image',
  'text',
  'badge',
  'spacer',
] as const;
export type BlockPresetId = (typeof BLOCK_PRESETS)[number];

/** A block from the palette. `sectionId` is the section it should start on, when it takes one. */
export function presetBlock(preset: BlockPresetId, id: string, sectionId: string | null = null): Block {
  const next = { kind: 'next', sectionId } as const;
  switch (preset) {
    case 'photo-left':
      return makeBlock('item', id, { source: next, photo: 'left', photoSize: 42, colSpan: 2, nameSize: 36 });
    case 'photo-right':
      return makeBlock('item', id, { source: next, photo: 'right', photoSize: 42, colSpan: 2, nameSize: 36 });
    case 'photo-top':
      return makeBlock('item', id, { source: next, photo: 'top', photoSize: 58, align: 'center' });
    case 'photo-full':
      return makeBlock('item', id, {
        source: next,
        photo: 'background',
        nameColor: '#ffffff',
        textColor: '#ffffff',
        priceStyle: 'pill',
        priceColor: 'theme:bg',
        box: { background: 'theme:surface', padding: 24 },
      });
    case 'photo-round':
      return makeBlock('item', id, {
        source: next,
        photo: 'top',
        photoShape: 'circle',
        photoSize: 52,
        align: 'center',
        priceStyle: 'circle',
        priceColor: 'theme:bg',
        box: { background: null, padding: 12 },
      });
    case 'price-pill':
      return makeBlock('item', id, {
        source: next,
        photo: 'left',
        photoShape: 'circle',
        photoSize: 38,
        colSpan: 2,
        priceStyle: 'pill',
        priceColor: 'theme:bg',
        box: { borderWidth: 3, radius: 90, background: 'theme:surface', padding: 14 },
      });
    case 'list':
      return makeBlock('list', id, { sectionId, box: { background: null, padding: 12 } });
    case 'list-bordered':
      return makeBlock('list', id, { sectionId, box: { borderWidth: 3, background: null, radius: 24, padding: 26 } });
    case 'heading':
      return makeBlock('heading', id, { bind: sectionId ? 'section' : 'text', sectionId, text: 'Our menu', textAr: 'قائمتنا' });
    case 'title':
      return makeBlock('heading', id, { bind: 'title', size: 76, ornament: 'underline' });
    case 'image':
      return makeBlock('image', id);
    case 'text':
      return makeBlock('text', id, { text: 'All prices include tax', textAr: 'جميع الأسعار شاملة الضريبة' });
    case 'badge':
      return makeBlock('badge', id);
    case 'spacer':
    default:
      return makeBlock('spacer', id);
  }
}

function layoutOf(columns: number, portraitColumns: number, blocks: Block[], extra: Partial<BlocksLayout> = {}): BlocksLayout {
  return {
    version: BLOCKS_LAYOUT_VERSION,
    columns,
    portraitColumns,
    rows: null,
    portraitRows: null,
    gap: 20,
    padding: 40,
    background: { color: null, mediaId: null, overlay: 0 },
    blocks,
    ...extra,
  };
}

/** What a board without a layout of its own is drawn with: photo cards for every item. */
export function defaultBlocksLayout(): BlocksLayout {
  return layoutOf(
    4,
    2,
    Array.from({ length: 8 }, (_, i) => presetBlock('photo-top', `card-${i + 1}`)),
  );
}

/** One-click starting points for the editor, filled from the board's own sections. */
export const BLOCKS_STARTING_LAYOUTS = ['cards', 'lists', 'hero-list', 'title-grid', 'blank'] as const;
export type BlocksStartingLayout = (typeof BLOCKS_STARTING_LAYOUTS)[number];

export function startingBlocksLayout(kind: BlocksStartingLayout, sections: Pick<MenuSection, 'id'>[]): BlocksLayout {
  const s = (i: number) => sections[i]?.id ?? sections[0]?.id ?? null;
  switch (kind) {
    case 'cards':
      return layoutOf(2, 2, [
        presetBlock('photo-left', 'card-1'),
        presetBlock('photo-left', 'card-2'),
        presetBlock('photo-left', 'card-3'),
        presetBlock('photo-left', 'card-4'),
      ].map((b) => ({ ...b, colSpan: 1 })));
    case 'lists': {
      // One list per section, up to three; a board without sections gets one list of everything.
      const count = Math.max(1, Math.min(3, sections.length));
      return layoutOf(
        count,
        1,
        Array.from({ length: count }, (_, i) => ({ ...presetBlock('list-bordered', `list-${i + 1}`, sections.length ? s(i) : null), colSpan: 1 })),
      );
    }
    case 'hero-list':
      return layoutOf(3, 2, [
        { ...presetBlock('photo-full', 'hero', s(0)), colSpan: 2, rowSpan: 2, nameSize: 60, priceSize: 52 } as Block,
        { ...presetBlock('list', 'list-1', sections.length ? s(0) : null), colSpan: 1, rowSpan: 2 } as Block,
      ]);
    case 'title-grid':
      return layoutOf(4, 2, [
        presetBlock('title', 'title'),
        ...Array.from({ length: 8 }, (_, i) => presetBlock('photo-top', `card-${i + 1}`)),
      ]);
    case 'blank':
    default:
      return layoutOf(3, 2, []);
  }
}

/** The "Build your own" sample board's layout; section ids are the starter's section keys. */
export const STARTER_BLOCKS_LAYOUT: BlocksLayout = layoutOf(4, 2, [
  { ...presetBlock('title', 'title'), size: 64 } as Block,
  { ...presetBlock('photo-full', 'hero', 'burgers'), colSpan: 2, rowSpan: 2, nameSize: 58, priceSize: 50, textSize: 22 } as Block,
  { ...presetBlock('list-bordered', 'sides', 'sides'), colSpan: 1, rowSpan: 2, nameSize: 26, headingSize: 34 } as Block,
  { ...presetBlock('list-bordered', 'drinks', 'drinks'), colSpan: 1, rowSpan: 2, nameSize: 26, headingSize: 34 } as Block,
  presetBlock('photo-left', 'card-1', 'burgers'),
  { ...presetBlock('price-pill', 'card-2', 'burgers'), colSpan: 1, nameSize: 28, priceSize: 28, showDescription: false } as Block,
  presetBlock('badge', 'combo'),
  { ...presetBlock('text', 'note'), colSpan: 4, box: { ...NO_BOX, padding: 4 }, size: 22 } as Block,
]);
